import React, { useEffect, useState } from "react";
import {
    Plus,
    Filter,
    Search,
    FileText,
    Calendar,
    User,
    Clock,
    CheckCircle,
    XCircle,
    MoreHorizontal,
    Badge,
    Laptop,
    Settings
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { useUserInfo } from "@/hooks/useUserInfo";
import { getMyRequests, getSummary } from "@/utils/pasUtils.js";
import { string } from "zod";
import Pagination from "@/components/Pagination"; // Add this import, adjust the path if needed
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";

const Requests: React.FC = () => {
    const VITE_USERNAMET = import.meta.env.VITE_USERNAMET
    const VITE_PASSWORD = import.meta.env.VITE_PASSWORD

    const { t } = useLanguage();
    const { fullName, email, profile } = useUserInfo();
    const [isLoading, setIsLoading] = useState(true);
    const [paginationNumbers, setPaginationNumbers] = useState<(number | string)[]>([1, 2, 3, '...', 10]);
    const [currentPage, setCurrentPage] = useState(1);
    const [paginationNumbersPermission, setPaginationNumbersPermission] = useState<(number | string)[]>([1, 2, 3, '...', 10]);
    const [currentPagePermission, setCurrentPagePermission] = useState(1);
    const [paginationNumbersWork_Outside, setPaginationNumbersWork_Outside] = useState<(number | string)[]>([1, 2, 3, '...', 10]);
    const [currentPageWork_Outside, setCurrentPageWork_Outside] = useState(1);
    const [paginationNumbersTechnical_Support, setPaginationNumbersTechnical_Support] = useState<(number | string)[]>([1, 2, 3, '...', 10]);
    const [currentPageTechnical_Support, setCurrentPageTechnical_Support] = useState(1);
    const [requests, setRequests] = useState<Request>({
        leaves: [],
        work_outside: [],
        permissions: [],
        it_support_request: []
    });
    // Define a Request type above (or import it if already defined)
    type Request_Leave = {
        employee_name: string;
        from_date: string;
        leave_type: string;
        name: string;
        to_date: string;
        total_leave_days: string;
        workflow_state: string;
    };
    type Request_Permission = {
        day: string;
        end_time: string;
        name: string;
        start_time: string;
        total_hours: string;
        workflow_state: string;
    };
    type Request_Work_Outside = {
        employee_name: string;
        from_date: string;
        name: string;
        to_date: string;
        total_days: string;
        workflow_state: string;
    };
    type Request_Technical_Support = {
        details: string;
        employee: string;
        employee_name: string;
        status: string;
        subject: string;
        support_order_type: string;
        support_type: string;
        workflow_state: string;
    }

    interface Request {
        leaves: Request_Leave[];
        work_outside: Request_Work_Outside[];
        permissions: Request_Permission[];
        it_support_request: Request_Technical_Support[]
    }

    const [filteredRequests, setFilteredDocuments] = useState<Request>({
        leaves: [],
        work_outside: [],
        permissions: [],
        it_support_request: [],
    });
    const [data, setData] = useState({
        profile: null,
        isLoading: true,
        error: null,
    });

    useEffect(() => {
        setIsLoading(true);

        async function fetchSalaryData() {
            try {
                const summaryResponse = await getSummary(
                    VITE_USERNAMET,
                    VITE_PASSWORD,
                    email
                );
                if (data.isLoading) {
                    setData((prev) => ({
                        ...prev,
                        profile: summaryResponse.profile,
                        isLoading: false,
                        error: null,
                    }));
                    console.log(summaryResponse);
                    const requestsResponse = await getMyRequests(
                        VITE_USERNAMET,
                        VITE_PASSWORD,
                        summaryResponse.profile?.name
                    );
                    setRequests(requestsResponse);
                    setPaginationNumbers(generatePaginationNumbers(1, Math.ceil(requestsResponse?.leaves?.length / 10)));//[1, 2, 3, '...', Math.ceil(requestsResponse?.leaves?.length / 10)]); // Assuming 30 records per page
                    setPaginationNumbersPermission(generatePaginationNumbersPermission(1, Math.ceil(requestsResponse?.permissions?.length / 10)));//[1, 2, 3, '...', Math.ceil(requestsResponse?.permissions?.length / 10)]); // Assuming 30 records per page
                    setPaginationNumbersWork_Outside(generatePaginationNumbersWork_Outside(1, Math.ceil(requestsResponse?.work_outside?.length / 10)));
                    setPaginationNumbersTechnical_Support(generatePaginationNumbersTechnical_Support(1, Math.ceil(requestsResponse?.it_support_request?.length / 10)));
                    console.log(requestsResponse);
                    setFilteredDocuments(requestsResponse);
                }
            } catch (err) {
                setData((prev) => ({
                    ...prev,
                    isLoading: false,
                    error: err.message,
                }));
            } finally {
                setIsLoading(false);
                // if (!data.isLoading)
                //   console.log(JSON.stringify(profile));
            }
        }

        fetchSalaryData();
    }, [data, data.isLoading, email, profile]);

    //Leaves
    const handlePageChange = (page: number) => {
        setCurrentPage(page);

        const totalPages = Math.ceil(filteredRequests?.leaves?.length / 10); // Assuming 10 records per page
        const paginationNumbers = generatePaginationNumbers(page, totalPages);

        setPaginationNumbers(paginationNumbers);
    };
    const generatePaginationNumbers = (currentPage: number, totalPages: number): (number | string)[] => {
        if (totalPages <= 5) {
            // If total pages is 5 or less, show all pages
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        const paginationNumbers: (number | string)[] = [];

        // Always show first page
        paginationNumbers.push(1);

        // Calculate the range for middle pages (1 before and 1 after current page)
        const startPage = Math.max(2, currentPage - 1);
        const endPage = Math.min(totalPages - 1, currentPage + 1);

        // Add first ellipsis if there's a gap between page 1 and startPage
        if (startPage > 2) {
            paginationNumbers.push('...');
        }

        // Add middle pages (1 before and 1 after current page)
        for (let i = startPage; i <= endPage; i++) {
            paginationNumbers.push(i);
        }

        // Add second ellipsis if there's a gap between endPage and last page
        if (endPage < totalPages - 1) {
            paginationNumbers.push('...');
        }

        // Always show last page if it's different from first page
        if (totalPages > 1) {
            paginationNumbers.push(totalPages);
        }

        return paginationNumbers;
    };
    //Permissions
    const handlePageChangePermission = (page: number) => {
        setCurrentPagePermission(page);

        const totalPages = Math.ceil(filteredRequests?.permissions?.length / 10); // Assuming 10 records per page
        const paginationNumbers = generatePaginationNumbers(page, totalPages);

        setPaginationNumbersPermission(paginationNumbers);
    };
    const generatePaginationNumbersPermission = (currentPage: number, totalPages: number): (number | string)[] => {
        if (totalPages <= 5) {
            // If total pages is 5 or less, show all pages
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        const paginationNumbers: (number | string)[] = [];

        // Always show first page
        paginationNumbers.push(1);

        // Calculate the range for middle pages (1 before and 1 after current page)
        const startPage = Math.max(2, currentPage - 1);
        const endPage = Math.min(totalPages - 1, currentPage + 1);

        // Add first ellipsis if there's a gap between page 1 and startPage
        if (startPage > 2) {
            paginationNumbers.push('...');
        }

        // Add middle pages (1 before and 1 after current page)
        for (let i = startPage; i <= endPage; i++) {
            paginationNumbers.push(i);
        }

        // Add second ellipsis if there's a gap between endPage and last page
        if (endPage < totalPages - 1) {
            paginationNumbers.push('...');
        }

        // Always show last page if it's different from first page
        if (totalPages > 1) {
            paginationNumbers.push(totalPages);
        }

        return paginationNumbers;
    };
    //Work Outside
    const handlePageChangeWork_Outside = (page: number) => {
        setCurrentPageWork_Outside(page);

        const totalPages = Math.ceil(filteredRequests?.work_outside?.length / 10); // Assuming 10 records per page
        const paginationNumbers = generatePaginationNumbersWork_Outside(page, totalPages);

        setPaginationNumbersWork_Outside(paginationNumbers);
    };
    const generatePaginationNumbersWork_Outside = (currentPage: number, totalPages: number): (number | string)[] => {
        if (totalPages <= 5) {
            // If total pages is 5 or less, show all pages
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        const paginationNumbers: (number | string)[] = [];

        // Always show first page
        paginationNumbers.push(1);

        // Calculate the range for middle pages (1 before and 1 after current page)
        const startPage = Math.max(2, currentPage - 1);
        const endPage = Math.min(totalPages - 1, currentPage + 1);

        // Add first ellipsis if there's a gap between page 1 and startPage
        if (startPage > 2) {
            paginationNumbers.push('...');
        }

        // Add middle pages (1 before and 1 after current page)
        for (let i = startPage; i <= endPage; i++) {
            paginationNumbers.push(i);
        }

        // Add second ellipsis if there's a gap between endPage and last page
        if (endPage < totalPages - 1) {
            paginationNumbers.push('...');
        }

        // Always show last page if it's different from first page
        if (totalPages > 1) {
            paginationNumbers.push(totalPages);
        }

        return paginationNumbers;
    };
    //Technical Support
    const handlePageChangeTechnical_Support = (page: number) => {
        setCurrentPageTechnical_Support(page);

        const totalPages = Math.ceil(filteredRequests?.it_support_request?.length / 10); // Assuming 10 records per page
        const paginationNumbers = generatePaginationNumbersTechnical_Support(page, totalPages);

        setPaginationNumbersTechnical_Support(paginationNumbers);
    };
    const generatePaginationNumbersTechnical_Support = (currentPage: number, totalPages: number): (number | string)[] => {
        if (totalPages <= 5) {
            // If total pages is 5 or less, show all pages
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        const paginationNumbers: (number | string)[] = [];

        // Always show first page
        paginationNumbers.push(1);

        // Calculate the range for middle pages (1 before and 1 after current page)
        const startPage = Math.max(2, currentPage - 1);
        const endPage = Math.min(totalPages - 1, currentPage + 1);

        // Add first ellipsis if there's a gap between page 1 and startPage
        if (startPage > 2) {
            paginationNumbers.push('...');
        }

        // Add middle pages (1 before and 1 after current page)
        for (let i = startPage; i <= endPage; i++) {
            paginationNumbers.push(i);
        }

        // Add second ellipsis if there's a gap between endPage and last page
        if (endPage < totalPages - 1) {
            paginationNumbers.push('...');
        }

        // Always show last page if it's different from first page
        if (totalPages > 1) {
            paginationNumbers.push(totalPages);
        }

        return paginationNumbers;
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case "pending":
                return <Clock className="h-4 w-4 text-warning" />;
            case "approved":
            case "completed":
                return <CheckCircle className="h-4 w-4 text-success" />;
            case "rejected":
                return <XCircle className="h-4 w-4 text-destructive" />;
            default:
                return <Clock className="h-4 w-4 text-warning" />;
        }
    };

    const getStatusBadge = (status: string) => {
        const isApproved =
            status.toLowerCase().includes("approved") ||
            status.toLowerCase().includes("completed");
        const isRejected = status.toLowerCase().includes("rejected");
        const isPending = status.toLowerCase().includes("pending");

        if (isApproved) {
            return (
                <span className="bg-success/10 text-success text-xs border-0 px-2 py-1 rounded flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    {status}
                </span>
            );
        }

        if (isRejected) {
            return (
                <span
                    className="bg-destructive/10 text-destructive text-xs border-0 px-2 py-1 rounded flex items-center gap-1">
                    <XCircle className="h-3 w-3" />
                    {status}
                </span>
            );
        }

        if (isPending) {
            return (
                <span className="bg-warning/10 text-warning text-xs border-0 px-2 py-1 rounded flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {status}
                </span>
            );
        }

        // Default case
        return (
            <span className="bg-muted text-muted-foreground border-0 text-xs px-2 py-1 rounded">
                {status}
            </span>
        );
    };


    return (
        <div className="space-y-6 fade-in">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">{t("nav.requests")}</h1>
                    <p className="text-muted-foreground mt-1">
                        {t("requests.track_requests")}
                    </p>
                </div>
                {/*<Button className="btn-primary">*/}
                {/*  <Plus className="h-4 w-4 me-2" />*/}
                {/*  {t("requests.new")}*/}
                {/*</Button>*/}
            </div>

            {!isLoading ? (
                <Card className="card-primary">
                    <CardTitle className="flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-primary" />
                        {t("requests.title")}
                    </CardTitle>
                    <CardContent className="space-y-4">



                        {isLoading ? (
                            <Card className="card-primary">
                                <CardTitle className="flex items-center gap-2">
                                    <Calendar className="h-5 w-5 text-primary" />
                                    الطلبات
                                </CardTitle>
                                <CardContent className="space-y-4">
                                    <div className="inset-0 flex flex-col items-center justify-center bg-background z-50">
                                        <div className="flex flex-col items-center gap-4 animate-pulse">
                                            <div className="w-16 h-16 bg-muted rounded-full"></div>
                                            <div className="space-y-3 text-center">
                                                <div className="h-5 w-40 bg-muted rounded mx-auto"></div>
                                                <div className="h-4 w-24 bg-muted rounded mx-auto"></div>
                                                <div className="h-4 w-24 bg-muted rounded mx-auto"></div>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ) : (
                            <>
                                {/* Leaves Section */}
                                <Accordion type="single" collapsible className="w-full">
                                    <AccordionItem key="leaves" value={`item-${0}`}>
                                        <AccordionTrigger className="text-start text-base font-bold py-5 hover:no-underline hover:text-primary">
                                            <div className="flex items-center gap-3">
                                                <span className="font-bold">طلبات الإجازات</span>
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent className="text-right">
                                            <div className="pb-2">
                                                <Card className="border-0 bg-muted/70">
                                                    <CardHeader>
                                                        <CardTitle className="flex items-center gap-2">
                                                            <Calendar className="h-5 w-5 text-primary" />
                                                            طلبات الإجازات
                                                            <span className="text-sm font-normal text-muted-foreground">
                                                                ({filteredRequests?.leaves?.length})
                                                            </span>
                                                        </CardTitle>
                                                    </CardHeader>
                                                    <CardContent className="space-y-4">
                                                        {filteredRequests?.leaves?.length === 0 ? (
                                                            <div className="text-center py-8">
                                                                <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                                                <h3 className="text-lg font-medium mb-2">لا توجد طلبات إجازات</h3>
                                                                <p className="text-muted-foreground">
                                                                    لم يتم العثور على طلبات إجازات
                                                                </p>
                                                            </div>
                                                        ) : (
                                                            filteredRequests?.leaves?.slice((currentPage - 1) * 10, currentPage * 10).map((request, index) => (
                                                                <div
                                                                    key={index}
                                                                    className="p-3 sm:p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                                                                >
                                                                    <div className="flex items-start justify-between gap-2">
                                                                        <div className="flex-1 min-w-0">
                                                                            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3">
                                                                                <span className="text-xs font-mono bg-primary/10 text-primary px-2 py-1 rounded">
                                                                                    {request.name}
                                                                                </span>
                                                                                {getStatusBadge(request.workflow_state)}
                                                                            </div>

                                                                            <h3 className="text-base sm:text-lg font-semibold mb-2 break-words">
                                                                                {request.leave_type}
                                                                            </h3>

                                                                            <div
                                                                                className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-muted-foreground">
                                                                                <div className="flex items-center gap-2">
                                                                                    <Calendar className="h-4 w-4" />
                                                                                    <span>من: {request.from_date}</span>
                                                                                </div>
                                                                                <div className="flex items-center gap-2">
                                                                                    <Calendar className="h-4 w-4" />
                                                                                    <span>إلى: {request.to_date}</span>
                                                                                </div>
                                                                                <div className="flex items-center gap-2">
                                                                                    <Clock className="h-4 w-4" />
                                                                                    <span>المدة: {request.total_leave_days} أيام</span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        )}
                                                        <div>
                                                            <Pagination
                                                                currentPage={currentPage}
                                                                paginationNumbers={paginationNumbers} // Replace with your pagination logic
                                                                hasPreviousPage={currentPage > 1}
                                                                hasNextPage={currentPage < Math.ceil(filteredRequests?.leaves?.length / 10)} // Replace with your total pages logic
                                                                onPageChange={handlePageChange}
                                                            />
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>
                                </Accordion>

                                {/* Permissions Section */}
                                <Accordion type="single" collapsible className="w-full">
                                    <AccordionItem key="leaves" value={`item-${0}`}>
                                        <AccordionTrigger className="text-start text-base font-bold py-5 hover:no-underline hover:text-primary">
                                            <div className="flex items-center gap-3">
                                                <span className="font-bold">طلبات الأذونات</span>
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent className="text-right">
                                            <div className="pb-2">
                                                <Card className="border-0 bg-muted/70">
                                                    <CardHeader>
                                                        <CardTitle className="flex items-center gap-2">
                                                            <Clock className="h-5 w-5 text-primary" />
                                                            طلبات الأذونات
                                                            <span className="text-sm font-normal text-muted-foreground">
                                                                ({filteredRequests?.permissions?.length})
                                                            </span>
                                                        </CardTitle>
                                                    </CardHeader>
                                                    <CardContent className="space-y-4">
                                                        {filteredRequests?.permissions?.length === 0 ? (
                                                            <div className="text-center py-8">
                                                                <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                                                <h3 className="text-lg font-medium mb-2">لا توجد طلبات أذونات</h3>
                                                                <p className="text-muted-foreground">
                                                                    لم يتم العثور على طلبات أذونات
                                                                </p>
                                                            </div>
                                                        ) : (
                                                            filteredRequests?.permissions?.slice((currentPagePermission - 1) * 10, currentPagePermission * 10).map((request, index) => (
                                                                <div
                                                                    key={index}
                                                                    className="p-3 sm:p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                                                                >
                                                                    <div className="flex items-start justify-between gap-2">
                                                                        <div className="flex-1 min-w-0">
                                                                            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3">
                                                                                <span className="text-xs font-mono bg-primary/10 text-primary px-2 py-1 rounded">
                                                                                    {request.name}
                                                                                </span>
                                                                                {getStatusBadge(request.workflow_state)}
                                                                            </div>

                                                                            <h3 className="text-base sm:text-lg font-semibold mb-2 break-words">
                                                                                {t("requests.permission_request")}
                                                                            </h3>

                                                                            <div
                                                                                className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-muted-foreground">
                                                                                <div className="flex items-center gap-2">
                                                                                    <Calendar className="h-4 w-4" />
                                                                                    <span>التاريخ: {request.day}</span>
                                                                                </div>
                                                                                <div className="flex items-center gap-2">
                                                                                    <Clock className="h-4 w-4" />
                                                                                    <span>من: {request.start_time}</span>
                                                                                </div>
                                                                                <div className="flex items-center gap-2">
                                                                                    <Clock className="h-4 w-4" />
                                                                                    <span>إلى: {request.end_time}</span>
                                                                                </div>
                                                                            </div>

                                                                            {request.total_hours && (
                                                                                <div className="mt-3 text-sm">
                                                                                    <span className="font-medium">
                                                                                        المدة الإجمالية: {request.total_hours} ساعة
                                                                                    </span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        )}
                                                        <div>
                                                            <Pagination
                                                                currentPage={currentPagePermission}
                                                                paginationNumbers={paginationNumbersPermission} // Replace with your pagination logic
                                                                hasPreviousPage={currentPagePermission > 1}
                                                                hasNextPage={currentPagePermission < Math.ceil(filteredRequests?.permissions?.length / 10)} // Replace with your total pages logic
                                                                onPageChange={handlePageChangePermission}
                                                            />
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>
                                </Accordion>
                                {/* Work Outside Section */}
                                <Accordion type="single" collapsible className="w-full">
                                    <AccordionItem key="leaves" value={`item-${0}`}>
                                        <AccordionTrigger className="text-start text-base font-bold py-5 hover:no-underline hover:text-primary">
                                            <div className="flex items-center gap-3">
                                                <span className="font-bold">طلبات العمل خارج المكتب</span>
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent className="text-right">
                                            <div className="pb-2">
                                                <Card className="border-0 bg-muted/70">
                                                    <CardHeader>
                                                        <CardTitle className="flex items-center gap-2">
                                                            <Laptop className="h-5 w-5 text-primary" />
                                                            طلبات العمل خارج المكتب
                                                            <span className="text-sm font-normal text-muted-foreground">
                                                                ({filteredRequests?.work_outside?.length})
                                                            </span>
                                                        </CardTitle>
                                                    </CardHeader>
                                                    <CardContent className="space-y-4">
                                                        {filteredRequests?.work_outside?.length === 0 ? (
                                                            <div className="text-center py-8">
                                                                <Laptop className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                                                <h3 className="text-lg font-medium mb-2">
                                                                    لا توجد طلبات عمل خارج المكتب
                                                                </h3>
                                                                <p className="text-muted-foreground">
                                                                    لم يتم العثور على طلبات عمل خارج المكتب
                                                                </p>
                                                            </div>
                                                        ) : (
                                                            filteredRequests?.work_outside?.slice((currentPageWork_Outside - 1) * 10, currentPageWork_Outside * 10).map((request, index) => (
                                                                <div
                                                                    key={index}
                                                                    className="p-3 sm:p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                                                                >
                                                                    <div className="flex items-start justify-between gap-2">
                                                                        <div className="flex-1 min-w-0">
                                                                            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3">
                                                                                <span className="text-xs font-mono bg-primary/10 text-primary px-2 py-1 rounded">
                                                                                    {request.name}
                                                                                </span>
                                                                                {getStatusBadge(request.workflow_state)}
                                                                            </div>

                                                                            <h3 className="text-base sm:text-lg font-semibold mb-2 break-words">
                                                                                {t("requests.work_outside_request")}
                                                                            </h3>

                                                                            <div
                                                                                className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-muted-foreground">
                                                                                <div className="flex items-center gap-2">
                                                                                    <Calendar className="h-4 w-4" />
                                                                                    <span>من: {request.from_date}</span>
                                                                                </div>
                                                                                <div className="flex items-center gap-2">
                                                                                    <Calendar className="h-4 w-4" />
                                                                                    <span>إلى: {request.to_date}</span>
                                                                                </div>
                                                                                <div className="flex items-center gap-2">
                                                                                    <Clock className="h-4 w-4" />
                                                                                    <span>
                                                                                        المدة: {request.total_days || "غير محدد"} أيام
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        )}
                                                        <div>
                                                            <Pagination
                                                                currentPage={currentPageWork_Outside}
                                                                paginationNumbers={paginationNumbersWork_Outside} // Replace with your pagination logic
                                                                hasPreviousPage={currentPageWork_Outside > 1}
                                                                hasNextPage={currentPageWork_Outside < Math.ceil(filteredRequests?.work_outside?.length / 10)} // Replace with your total pages logic
                                                                onPageChange={handlePageChangeWork_Outside}
                                                            />
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>
                                </Accordion>
                                {/* Technical Support */}
                                <Accordion type="single" collapsible className="w-full">
                                    <AccordionItem key="leaves" value={`item-${0}`}>
                                        <AccordionTrigger className="text-start text-base font-bold py-5 hover:no-underline hover:text-primary">
                                            <div className="flex items-center gap-3">
                                                <span className="font-bold">طلبات الدعم الفني</span>
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent className="text-right">
                                            <div className="pb-2">
                                                <Card className="border-0 bg-muted/70">
                                                    <CardHeader>
                                                        <CardTitle className="flex items-center gap-2">
                                                            <Settings className="h-5 w-5 text-primary" />
                                                            طلبات الدعم الفني
                                                            <span className="text-sm font-normal text-muted-foreground">
                                                                ({filteredRequests?.it_support_request?.length})
                                                            </span>
                                                        </CardTitle>
                                                    </CardHeader>
                                                    <CardContent className="space-y-4">
                                                        {filteredRequests?.it_support_request?.length === 0 ? (
                                                            <div className="text-center py-8">
                                                                <Settings className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                                                <h3 className="text-lg font-medium mb-2">
                                                                    لا توجد طلبات دعم فني
                                                                </h3>
                                                                <p className="text-muted-foreground">
                                                                    لم يتم العثور على طلبات دعم فني
                                                                </p>
                                                            </div>
                                                        ) : (
                                                            filteredRequests?.it_support_request?.slice((currentPageTechnical_Support - 1) * 10, currentPageTechnical_Support * 10).map((request, index) => (
                                                                <div
                                                                    key={index}
                                                                    className="p-3 sm:p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                                                                >
                                                                    <div className="flex items-start justify-between gap-2">
                                                                        <div className="flex-1 min-w-0">
                                                                            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3">
                                                                                <span className="text-xs font-mono bg-primary/10 text-primary px-2 py-1 rounded">
                                                                                    {request.employee_name}
                                                                                </span>
                                                                                {getStatusBadge(request.workflow_state)}
                                                                            </div>

                                                                            <h3 className="text-base sm:text-lg font-semibold mb-2 break-words">
                                                                                {t("requests.technical_support_request")}
                                                                            </h3>
                                                                            <h3 className="mb-2">
                                                                                الموضوع: {request.subject}
                                                                            </h3>
                                                                            <div className="flex items-center gap-2">
                                                                                <Calendar className="h-4 w-4" />
                                                                                <span>نوع أمر الدعم: {request.support_order_type}</span>
                                                                            </div>


                                                                            <div
                                                                                className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-muted-foreground">
                                                                                <div className="flex items-center gap-2">
                                                                                    <Calendar className="h-4 w-4" />
                                                                                    <span>نوع أمر الدعم: {request.support_order_type}</span>
                                                                                </div>
                                                                                <div className="flex items-center gap-2">
                                                                                    <Calendar className="h-4 w-4" />
                                                                                    <span>نوع الدعم: {request.support_type}</span>
                                                                                </div>
                                                                                <div className="flex items-center gap-2">
                                                                                    <Clock className="h-4 w-4" />
                                                                                    <span>
                                                                                        الحالة: {request.status || "غير محدد"}
                                                                                    </span>
                                                                                </div>
                                                                                <div>
                                                                                    التفاصيل: <div
                                                                                        className="quill-content"
                                                                                        dangerouslySetInnerHTML={{ __html: request?.details }}
                                                                                    />
                                                                                </div>

                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        )}
                                                        <div>
                                                            <Pagination
                                                                currentPage={currentPageTechnical_Support}
                                                                paginationNumbers={paginationNumbersTechnical_Support} // Replace with your pagination logic
                                                                hasPreviousPage={currentPageTechnical_Support > 1}
                                                                hasNextPage={currentPageTechnical_Support < Math.ceil(filteredRequests?.it_support_request?.length / 10)} // Replace with your total pages logic
                                                                onPageChange={handlePageChangeTechnical_Support}
                                                            />
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>
                                </Accordion>
                            </>
                        )}
                    </CardContent>
                </Card >
            ) : <div className="inset-0 flex flex-col items-center justify-center bg-background z-50">
                <div className="flex flex-col items-center gap-4 animate-pulse">
                    <div className="w-16 h-16 bg-muted rounded-full"></div>
                    <div className="space-y-3 text-center">
                        <div className="h-5 w-40 bg-muted rounded mx-auto"></div>
                        <div className="h-4 w-24 bg-muted rounded mx-auto"></div>
                        <div className="h-4 w-24 bg-muted rounded mx-auto"></div>
                    </div>
                </div>
            </div>}
        </div >
    );
};

export default Requests;
